/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { apiClient } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, X, Mail, MapPin, Briefcase, Calendar, CheckCircle2,
  AlertCircle, Loader2, ChevronDown, ChevronUp, ExternalLink,
} from "lucide-react";

const STATUS_LABEL = {
  pending: "En attente de validation",
  active: "Activé",
  rejected: "Refusé",
};
const STATUS_TONE = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-50 text-rose-800 border-rose-200",
};

function fmtAmount(p) {
  if (!p || p.amount == null) return "—";
  const v = (p.amount / 100).toFixed(2);
  return `${v} ${p.currency?.toUpperCase() || "EUR"}`;
}
function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("fr-FR"); } catch { return "—"; }
}

export default function AdminPendingExperts() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await apiClient.get("/admin/experts/applications");
      setApps(data.applications || []);
    } catch (e) {
      setError(e?.response?.status === 403 ? "Accès réservé aux administrateurs." : "Chargement impossible.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    queueMicrotask(load);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const accept = async (id) => {
    if (!window.confirm("Activer ce profil spécialiste ? Le paiement sera capturé.")) return;
    setBusyId(id);
    try {
      await apiClient.post(`/admin/experts/${id}/verify`, { verified: true });
      toast.success("Profil activé");
      load();
    } catch { toast.error("Erreur d'activation"); }
    finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Motif du refus (sera enregistré) :");
    if (!reason || reason.trim().length < 4) return;
    if (!window.confirm("Confirmer le refus ? Un remboursement intégral sera marqué comme à effectuer.")) return;
    setBusyId(id);
    try {
      const { data } = await apiClient.post(`/admin/experts/${id}/reject`, { reason });
      toast.success(`Refus enregistré · ${data.refund_pending_count} paiement(s) marqué(s) pour remboursement`);
      load();
    } catch { toast.error("Erreur lors du refus"); }
    finally { setBusyId(null); }
  };

  const requestInfo = async (app) => {
    const msg = window.prompt(`Quel complément demander à ${app.first_name} ${app.last_name} ?`);
    if (!msg) return;
    // Pas encore d'endpoint dédié — on log un audit côté admin pour traçabilité.
    toast.info("Demande d'information envoyée par mail (à implémenter).");
  };

  const counts = apps.reduce((acc, a) => {
    const k = a.status || "pending";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="bg-[#F5F7FA] min-h-screen">
      <Navbar variant="light" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#C9A961] mb-2">Back-office · Adhésions</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[#0B3A2A]">
            Candidatures spécialistes en attente
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-2xl">
            Examinez chaque demande d'adhésion. Le paiement est capturé à l'inscription
            mais n'est définitif qu'après votre validation. Tout refus déclenche un ordre
            de remboursement intégral.
          </p>
          <div className="mt-4 flex gap-2 flex-wrap text-xs">
            <Badge variant="outline">À examiner : {counts.pending || 0}</Badge>
            <Badge variant="outline">Activés : {counts.active || 0}</Badge>
            <Badge variant="outline">Refusés : {counts.rejected || 0}</Badge>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 py-12">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        )}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 text-red-800 text-sm p-4">{error}</div>
        )}

        {!loading && apps.length === 0 && !error && (
          <Card className="p-8 text-center text-slate-600 border-slate-200">
            <CheckCircle2 className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
            Aucune candidature en attente.
          </Card>
        )}

        <div className="space-y-3" data-testid="admin-applications-list">
          {apps.map((a) => {
            const open = expanded === a.id;
            return (
              <Card
                key={a.id}
                className="border-slate-200 bg-white overflow-hidden"
                data-testid={`application-${a.id}`}
              >
                <button
                  type="button"
                  className="w-full p-4 flex items-center gap-4 text-left hover:bg-slate-50"
                  onClick={() => setExpanded(open ? null : a.id)}
                >
                  <div className="w-10 h-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sm font-medium text-slate-700">
                    {(a.first_name?.[0] || "").toUpperCase()}{(a.last_name?.[0] || "").toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900">
                      {a.first_name} {a.last_name}
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                      <span className="flex items-center gap-1 capitalize">
                        <Briefcase className="w-3 h-3" /> {a.profession || "—"}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {a.city || "—"}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" /> {fmtDate(a.created_at)}
                      </span>
                    </div>
                  </div>
                  <Badge className={`${STATUS_TONE[a.status] || ""} border rounded-full text-xs`}>
                    {STATUS_LABEL[a.status] || a.status}
                  </Badge>
                  <div className="text-right hidden sm:block">
                    <div className="text-xs text-slate-400">Paiement</div>
                    <div className="text-sm font-medium text-slate-700">{fmtAmount(a.payment)}</div>
                    {a.payment?.refund_pending && (
                      <div className="text-[10px] text-rose-600 mt-0.5">⟳ remboursement à exécuter</div>
                    )}
                  </div>
                  {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                </button>

                {open && (
                  <div className="border-t border-slate-100 p-4 bg-slate-50/40">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider mb-1">Email</div>
                        <div className="flex items-center gap-1 text-slate-800 truncate">
                          <Mail className="w-3.5 h-3.5 text-slate-400" /> {a.email || "—"}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider mb-1">Spécialités</div>
                        <div className="flex flex-wrap gap-1">
                          {(a.specialties || []).slice(0, 5).map((s, i) => (
                            <span key={i} className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-700">{s}</span>
                          ))}
                          {(!a.specialties || a.specialties.length === 0) && <span className="text-xs text-slate-400">—</span>}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider mb-1">Formule</div>
                        <div className="capitalize text-slate-800">{a.subscription || "—"}</div>
                      </div>
                      <div>
                        <div className="text-xs uppercase text-slate-500 tracking-wider mb-1">Paiement Stripe</div>
                        <div className="text-slate-800">
                          {a.payment?.stripe_session_id ? (
                            <a
                              href={`https://dashboard.stripe.com/payments/${a.payment.stripe_session_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-amber-700 hover:underline inline-flex items-center gap-1 text-xs"
                            >
                              Voir dans Stripe <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : "—"}
                        </div>
                      </div>
                      {a.rejected_reason && (
                        <div className="md:col-span-2">
                          <div className="text-xs uppercase text-slate-500 tracking-wider mb-1">Motif de refus</div>
                          <div className="text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-2 text-xs">
                            {a.rejected_reason}
                          </div>
                        </div>
                      )}
                    </div>

                    {a.status !== "active" && a.status !== "rejected" && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          disabled={busyId === a.id}
                          onClick={() => accept(a.id)}
                          data-testid={`accept-${a.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5"
                        >
                          <CheckCircle2 className="w-4 h-4" /> Accepter
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === a.id}
                          onClick={() => requestInfo(a)}
                          data-testid={`requestinfo-${a.id}`}
                          className="gap-1.5"
                        >
                          <AlertCircle className="w-4 h-4" /> Demander un complément
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === a.id}
                          onClick={() => reject(a.id)}
                          data-testid={`reject-${a.id}`}
                          className="text-rose-700 border-rose-200 hover:bg-rose-50 gap-1.5"
                        >
                          <X className="w-4 h-4" /> Refuser & rembourser
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <div className="mt-10 text-[11px] text-slate-400 flex items-center gap-1.5">
          <ShieldCheck className="w-3 h-3" />
          Workflow : paiement à l'inscription → vérification CONFILEX → activation (capture)
          ou refus (remboursement automatique des paiements liés).
        </div>
      </main>
      <Footer />
    </div>
  );
}
